import type { PlaycastKeyDetail, PlaycastMouseButton, PlaycastMouseState } from '@playcastdotio/CoreTypes';
import type { PlaycastMessage, PlaycastMessageTarget, PlaycastTouch, PlaycastTouchDetail } from '@playcastdotio/Messaging';

export type HeldInputRelease = { id: string } & (
  | { kind: 'keyboard'; detail: PlaycastKeyDetail }
  | { kind: 'mouse'; detail: PlaycastMouseButton }
  | { kind: 'touch'; detail: PlaycastTouch }
  | { kind: 'legacy-touch'; detail: PlaycastTouchDetail }
  | { kind: 'gamepad' }
);

/** Records accepted input only; it contains no transport or native input actuator. */
export class HeldInputLedger {
  private readonly held = new Map<string, HeldInputRelease>();
  private invalid = false;
  constructor(private readonly capacity = 300) {}

  observe(message: PlaycastMessage<PlaycastMessageTarget>): void {
    const target = message.header.target, action = message.header.action, body = message.body.message;
    try {
      if (target === 'keyboard') this.keyboard(action, body);
      else if (target === 'mouse') this.mouse(action, body);
      else if (target === 'touch') this.touch(action, body);
      else if (target === 'gamepad' && action === 'setState') this.put({ id: 'gamepad', kind: 'gamepad' });
    } catch { this.invalid = true; }
  }

  releases(): readonly HeldInputRelease[] { return [...this.held.values()].map(release => JSON.parse(JSON.stringify(release)) as HeldInputRelease); }
  has(id: string): boolean { return this.held.has(id); }
  safe(): boolean { return !this.invalid; }
  clear(): void { this.held.clear(); this.invalid = false; }

  /** Down/up identities also let guest bindings suppress a held key until a physical release. */
  transition(message: PlaycastMessage<PlaycastMessageTarget>): { id: string; pressed: boolean } | undefined {
    const body = message.body.message as unknown as Record<string, unknown>, action = message.header.action;
    if (!body || typeof body !== 'object') return undefined;
    if (message.header.target === 'keyboard' && (action === 'down' || action === 'up')) {
      return { id: `keyboard:${body['keyCode']}:${body['location']}`, pressed: action === 'down' };
    }
    if (message.header.target === 'mouse' && (action === 'down' || action === 'up')) {
      return { id: `mouse:${body['button']}`, pressed: action === 'down' };
    }
    return undefined;
  }
  transitions(message: PlaycastMessage<PlaycastMessageTarget>): readonly { id: string; pressed: boolean }[] {
    const transition = this.transition(message); if (transition) return [transition];
    if (message.header.target !== 'touch') return [];
    const action = message.header.action, body = message.body.message as unknown as Record<string, unknown>;
    if (action === 'down' || action === 'up') return [{ id: `legacy-touch:${body['touchId']}`, pressed: action === 'down' }];
    const changed = body['changedTouches'];
    return Array.isArray(changed) ? changed.slice(0, 32).map(touch => ({ id: `touch:${(touch as PlaycastTouch).identifier}`,
      pressed: action !== 'End' && action !== 'Cancel' })) : [];
  }

  private keyboard(action: string, value: unknown): void {
    if (action === 'setState') {
      const keys = (value as { keys: (PlaycastKeyDetail & { isPressed: boolean })[] }).keys;
      if (!Array.isArray(keys) || keys.length > 256) { this.invalid = true; return; }
      this.clearKind('keyboard');
      for (const key of keys) if (key.isPressed) this.keyboard('down', key);
      return;
    }
    if (action !== 'down' && action !== 'up') return;
    const input = value as PlaycastKeyDetail;
    const detail: PlaycastKeyDetail = { keyCode: input.keyCode, location: input.location, displayName: input.displayName,
      source: input.source, modifiers: input.modifiers };
    if (!Number.isInteger(detail.keyCode) || detail.keyCode < 0 || detail.keyCode > 65_535
      || !['standard', 'left', 'right', 'numpad'].includes(detail.location)) { this.invalid = true; return; }
    const id = `keyboard:${detail.keyCode}:${detail.location}`;
    if (action === 'up') this.held.delete(id); else this.put({ id, kind: 'keyboard', detail });
  }

  private mouse(action: string, value: unknown): void {
    if (action === 'setState') {
      const state = value as PlaycastMouseState;
      this.clearKind('mouse');
      for (const button of ['left', 'middle', 'right', 'back', 'forward'] as const) {
        if (state.buttons[button]?.isPressed) this.mouse('down', { ...state, button, clickCount: state.buttons.clickCount });
      }
      return;
    }
    if (action !== 'down' && action !== 'up') return;
    const input = value as PlaycastMouseButton;
    const detail: PlaycastMouseButton = { playerCoordinates: input.playerCoordinates, position: input.position, delta: input.delta,
      ...(input.positionFrame ? { positionFrame: input.positionFrame } : {}), button: input.button, clickCount: input.clickCount };
    if (!['left', 'middle', 'right', 'back', 'forward'].includes(detail.button)) { this.invalid = true; return; }
    const id = `mouse:${detail.button}`;
    if (action === 'up') this.held.delete(id); else this.put({ id, kind: 'mouse', detail });
  }

  private touch(action: string, value: unknown): void {
    if (action === 'down' || action === 'up') {
      const detail = value as PlaycastTouchDetail;
      if (!Number.isInteger(detail.touchId)) { this.invalid = true; return; }
      const id = `legacy-touch:${detail.touchId}`;
      if (action === 'up') this.held.delete(id); else this.put({ id, kind: 'legacy-touch', detail });
    } else if (['Start', 'End', 'Cancel', 'move'].includes(action)) {
      const touches = (value as { changedTouches: PlaycastTouch[] }).changedTouches;
      if (!Array.isArray(touches) || touches.length > 32) { this.invalid = true; return; }
      for (const detail of touches) {
        if (!Number.isInteger(detail.identifier)) { this.invalid = true; continue; }
        const id = `touch:${detail.identifier}`;
        if (action === 'End' || action === 'Cancel') this.held.delete(id); else this.put({ id, kind: 'touch', detail });
      }
    } else if (action === 'Flush') { this.clearKind('touch'); this.clearKind('legacy-touch'); }
  }

  private put(release: HeldInputRelease): void {
    if (!this.held.has(release.id) && this.held.size >= this.capacity) { this.invalid = true; return; }
    this.held.set(release.id, JSON.parse(JSON.stringify(release)) as HeldInputRelease);
  }
  private clearKind(kind: HeldInputRelease['kind']): void {
    for (const [id, release] of this.held) if (release.kind === kind) this.held.delete(id);
  }
}
